export const FINANCE_PAGES = {
  "commercial-mortgages": {
    metaTitle: "Commercial Mortgage Broker Melbourne | Invesmo",
    metaDescription: "Commercial property loans and refinancing arranged by a Melbourne commercial mortgage broker. Offices, warehouses, retail and industrial — 100+ lenders via LMG.",
    eyebrow: "Commercial Mortgages",
    h1: ["Commercial mortgage", "broker in", "Melbourne."],
    intro: "Whether you're buying your own premises, adding a commercial investment or refinancing for a sharper rate, we structure commercial property finance around your cash flow — not a bank's template.",
    bulletTitle: "What we arrange",
    bullets: [
      "Owner-occupier commercial property loans",
      "Commercial investment & lease-doc loans",
      "Commercial mortgage refinance & repricing",
      "Equity release for your next acquisition",
      "Low-doc & specialist lender options",
    ],
    body: [
      "As a commercial finance broker based in Camberwell, we arrange commercial property finance across Melbourne and greater Victoria — offices, warehouses, retail strips, industrial assets and mixed-use sites. Through the LMG Broker Services panel we access more than 100 lenders: major banks, second-tier banks and specialist commercial funders who price risk very differently from one another.",
      "Commercial lending is assessed differently from home lending — lease strength, WALE, asset quality and your business's trading position all shape the outcome. Decades of commercial and property experience means we package your application the way credit teams actually want to see it, and negotiate from a position of understanding both sides of the table.",
    ],
    faqs: [
      {
        q: "What are commercial property loan requirements in Australia?",
        a: "Typically a 25–35% deposit, demonstrated serviceability, a registered ABN with trading history, and a valuation of the security property. Lease-doc and low-doc options exist with specialist lenders for strong assets. We assess your position before you commit to a purchase.",
      },
      {
        q: "Can I refinance a commercial property loan?",
        a: "Yes — commercial mortgage refinance can sharpen your rate, release equity for the next acquisition, extend your term or consolidate facilities. We review existing facilities annually so you're never sitting on stale pricing.",
      },
      {
        q: "Do you only work in Melbourne?",
        a: "We're based in Camberwell and know the Melbourne market deeply, but we arrange commercial property finance Victoria-wide and interstate, in person, by phone or by video.",
      },
    ],
  },
  "medical-practice-finance": {
    metaTitle: "Medical Practice Finance Melbourne | Invesmo",
    metaDescription: "Commercial property finance for doctors: medical practice loans, premises purchase, clinic fit-out and practice acquisition finance from medico-policy lenders.",
    eyebrow: "Doctors & Medical Professionals",
    h1: ["Practice finance", "for doctors &", "medical professionals."],
    intro: "Medico lenders assess doctors differently — strong income trajectory, low default rates and portable skills unlock higher LVRs, waived LMI and sharper pricing. We know which lender policies apply to you.",
    bulletTitle: "What we arrange",
    bullets: [
      "Medical premises purchase — up to 90–100% with medico policy*",
      "Medical practice acquisition & goodwill loans",
      "Clinic fit-out & equipment finance",
      "Refinancing of practice and premises debt",
      "Home & investment loans with LMI waivers*",
    ],
    body: [
      "Buying your own medical centre or consulting rooms is often the defining financial move of a medical career. Specialist medico lenders will fund medical premises finance at far higher leverage than standard commercial policy — but only when the application is presented correctly: practice financials, AHPRA registration, income structure and the freehold-versus-goodwill split all matter.",
      "From your first registrar position through to owning multi-room practice premises, we map the lending pathway with you. Based in Melbourne, advising medical professionals Australia-wide.",
    ],
    faqs: [
      {
        q: "How do I finance the purchase of medical premises?",
        a: "Medico-policy lenders can fund up to 90–100% of the purchase for eligible doctors, with LMI waived. The structure usually splits freehold and goodwill, and serviceability leans on your practice income rather than payslips alone. We identify which lender's policy fits your stage of career.",
      },
      {
        q: "Can I get finance for a clinic fit-out?",
        a: "Yes — clinic fit-out and equipment finance is available as a separate facility, often with staged drawdowns matched to your builder's progress claims, so interest only accrues on what's drawn.",
      },
      {
        q: "Is it too early if I'm still a registrar?",
        a: "No. Medico lending pathways start early — home and investment lending with professional pricing is available from intern level, and practice premises finance typically opens up once you're established in practice.",
      },
    ],
  },
  "finance-for-accountants": {
    metaTitle: "Finance for Accountants & CPA Accountants Melbourne | Invesmo",
    metaDescription: "Commercial loans for accountants and CPA accountants: accounting practice acquisition finance, partner buy-in loans, premises and goodwill finance in Melbourne.",
    eyebrow: "Accountants & CPAs",
    h1: ["Finance for", "accountants &", "CPA accountants."],
    intro: "Fee books, goodwill, partner buy-ins, your own office — accounting practices have unique value drivers, and specialist lenders price them well when the story is told properly.",
    bulletTitle: "What we arrange",
    bullets: [
      "Accounting practice acquisition finance",
      "Partner buy-in & goodwill loans",
      "Office premises purchase or refinance",
      "Practice debt consolidation & restructuring",
      "Home & investment loans with professional pricing",
    ],
    body: [
      "Buying into or acquiring an accounting practice means lending against recurring fee income and goodwill — assets most bank credit teams struggle to assess. We work with lenders who understand practice valuation: fee-book multiples, client concentration, retention after transition and WIP cycles.",
      "As finance brokers for accountants in Melbourne, we also arrange commercial loans for your own office premises and professional-package home lending that recognises your income trajectory. Your own affairs deserve the same rigour you give your clients'.",
    ],
    faqs: [
      {
        q: "How do I finance the purchase of an accounting practice?",
        a: "Practice acquisition finance is typically structured against the fee book — lenders advance a multiple of recurring fees, with the balance covered by goodwill lending or equity. Strong client retention and a sensible transition period materially improve approval and pricing.",
      },
      {
        q: "Can I get finance for a partner buy-in?",
        a: "Yes — partner buy-in loans are a standard facility with professional-practice lenders. The practice's cash flow services the debt, and terms usually run 7–10 years matched to the partnership agreement.",
      },
      {
        q: "How do lenders assess professional practice income?",
        a: "They look at recurring fees, client concentration, debtor days, WIP and partner drawings rather than a simple payslip. Presenting two to three years of practice financials cleanly is where a specialist broker earns their fee.",
      },
    ],
  },
  "dental-practice-finance": {
    metaTitle: "Dental Practice Finance Melbourne | Invesmo",
    metaDescription: "Dental practice finance: acquisition loans, dental premises finance, equipment and chair finance, and clinic fit-out loans for dentists across Melbourne.",
    eyebrow: "Dentists",
    h1: ["Dental practice", "finance, done", "properly."],
    intro: "Chairs, CBCT machines, fit-outs and freeholds — dental practices carry serious capital needs, and dental-friendly lenders reward the profession's exceptional repayment record.",
    bulletTitle: "What we arrange",
    bullets: [
      "Dental practice acquisition loans",
      "Dental premises & commercial property finance",
      "Dental equipment & chair finance",
      "Clinic fit-out loans with staged drawdowns",
      "Refinance & expansion funding",
    ],
    body: [
      "Dental practice finance blends commercial property lending, goodwill and equipment finance into one capital plan. Specialist healthcare lenders offer dentists higher leverage on practice purchases and premises than generalist banks — often with LMI concessions — because dentistry's default rates are among the lowest of any profession.",
      "Whether you're an associate buying your first practice, fitting out a second surgery, or purchasing the building your clinic operates from, we structure the full stack so each facility is priced in the right market. Based in Melbourne, advising dentists Australia-wide.",
    ],
    faqs: [
      {
        q: "Can I finance dental equipment and chairs separately from the practice?",
        a: "Yes — equipment finance sits in its own facility with terms matched to the asset's life (typically 3–7 years), keeping your property loan clean and your cash flow predictable.",
      },
      {
        q: "Should I buy or lease my dental premises?",
        a: "Owning builds an asset alongside your practice and is often achievable at high leverage with healthcare lenders; leasing preserves capital for fit-out and growth. We model both against your numbers before you decide.",
      },
      {
        q: "I'm an associate — can I buy a practice?",
        a: "Practice acquisition finance is available to associates with solid production history. Lenders focus on your billings, the vendor's transition plan and patient retention.",
      },
    ],
  },
  "professional-practice-finance": {
    metaTitle: "Professional Practice Finance Melbourne | Invesmo",
    metaDescription: "Professional practice finance for lawyers, vets, pharmacists and allied health: acquisition loans, premises finance and partner buy-ins from specialist lenders.",
    eyebrow: "Other Professionals",
    h1: ["Practice finance", "for lawyers, vets", "& allied health."],
    intro: "Law firms, veterinary practices, pharmacies and allied health clinics all share one thing: their value lives in recurring client relationships — and specialist lenders will fund against it.",
    bulletTitle: "What we arrange",
    bullets: [
      "Legal practice acquisition & chambers finance",
      "Veterinary practice & premises loans",
      "Pharmacy finance (acquisition & fit-out)",
      "Allied health clinic loans — physio, chiro, psych",
      "Goodwill, partner buy-in & refinance facilities",
    ],
    body: [
      "Professional practice finance rewards specialist packaging. Lenders assess recurring revenue, client or patient retention, practitioner tenure and transition plans — criteria that differ sharply between a suburban vet clinic and a CBD law practice. We match your profession to the lenders whose policy actually fits it.",
      "From professional practice finance in Melbourne to regional and interstate purchases, we arrange acquisition loans, premises finance and partner buy-ins, and coordinate with your accountant and lawyer so the capital structure supports the deal structure.",
    ],
    faqs: [
      {
        q: "How do lenders assess professional practice income?",
        a: "They weight recurring client revenue, retention, practitioner tenure and industry margins over headline turnover. Two to three years of financials, plus a credible transition plan from the vendor, drive both approval and pricing.",
      },
      {
        q: "Should I buy or lease my practice premises?",
        a: "Buying converts rent into equity and suits established practices with stable location needs; leasing keeps capital free for acquisition and growth. Many professionals buy the premises personally (or via SMSF) and lease to their practice — we structure all three variants.",
      },
      {
        q: "Do professional packages apply to me?",
        a: "Most professions — legal, veterinary, pharmacy, allied health — qualify for professional pricing on home and investment lending, often with higher LVRs. Eligibility varies by lender; we check the whole panel.",
      },
    ],
  },
  "property-development-finance": {
    metaTitle: "Property Development Finance Melbourne | Invesmo",
    metaDescription: "Property development finance in Melbourne: construction finance, site acquisition, residual stock and mezzanine funding from a broker with development experience.",
    eyebrow: "Property Developers",
    h1: ["Property development", "finance in", "Melbourne."],
    intro: "Development funding is won or lost in the feasibility. With decades of hands-on property development experience, we structure your capital stack the way credit committees approve it.",
    bulletTitle: "What we arrange",
    bullets: [
      "Site acquisition & land banking finance",
      "Construction finance — duplex to multi-lot",
      "Residual stock & hold facilities",
      "Mezzanine & preferred equity",
      "Development refinance & equity release",
    ],
    body: [
      "As a development finance broker in Melbourne, we arrange construction finance across Victoria for residential and commercial projects — from dual-occupancy sites to multi-lot subdivisions. Senior debt through banks and non-banks, mezzanine where the leverage needs it, and residual stock facilities when you'd rather hold than sell into a soft market.",
      "Having developed property ourselves, we speak feasibility fluently: LVR versus LTC versus GDV, presale hurdles, quantity surveyor reports, contingency expectations. Your application lands on the credit desk already answering the questions they're paid to ask.",
    ],
    faqs: [
      {
        q: "How much can I borrow for a development?",
        a: "Senior lenders typically fund 65–80% of total development cost (LTC) or up to ~70% of gross realisable value (GRV), whichever binds. Mezzanine or preferred equity can stretch total leverage further. We model the stack against your feasibility before approaching lenders.",
      },
      {
        q: "Do I need presales?",
        a: "Bank construction finance usually requires presale coverage; non-bank and private lenders often don't — at a price. The right answer depends on your margin, timeline and exit strategy.",
      },
      {
        q: "Is this my first project — will anyone fund me?",
        a: "First projects are fundable with the right team around you: experienced builder, QS, and a feasibility that holds up. We help you assemble the lender-ready package.",
      },
    ],
  },
  "business-acquisition-finance": {
    metaTitle: "Business Acquisition Finance Australia | Invesmo",
    metaDescription: "Business acquisition finance: practice purchase loans, goodwill finance and partnership buy-in loans structured by an experienced commercial broker.",
    eyebrow: "Business Acquisitions",
    h1: ["Business acquisition", "finance,", "structured right."],
    intro: "Buying a business means lending against cash flow, goodwill and your plan for the transition — a story most banks won't read properly unless it's packaged by someone who's run businesses themselves.",
    bulletTitle: "What we arrange",
    bullets: [
      "Business purchase loans",
      "Goodwill & intangible asset finance",
      "Partnership buy-in loans",
      "Practice purchase finance (medical, dental, accounting, legal)",
      "Vendor-finance hybrid structures",
    ],
    body: [
      "Business acquisition finance blends term debt against cash flow, goodwill lending, and sometimes vendor finance or earn-out structures. The lenders who do this well are a small, specialist set — and each assesses goodwill, client concentration and key-person risk differently.",
      "Decades of running businesses and structuring trade means we pressure-test the deal before a lender does: serviceability under conservative assumptions, transition risk, and the working capital you'll need on day one. Then we take it to the lenders whose appetite matches.",
    ],
    faqs: [
      {
        q: "How do lenders value goodwill?",
        a: "Usually as a multiple of maintainable earnings, adjusted for client concentration, key-person dependence and industry norms. Recurring contracted revenue values highest; one-off project income discounts heavily.",
      },
      {
        q: "How much deposit do I need to buy a business?",
        a: "Typically 30–50% of the purchase price, though strong cash-flow businesses with contracted revenue can support higher leverage, and vendor finance can bridge part of the gap.",
      },
      {
        q: "Can you help with due diligence?",
        a: "We pressure-test the financials from a lender's perspective and coordinate with your accountant and lawyer — so finance approval and legal review move together, not sequentially.",
      },
    ],
  },
};

export const FINANCE_PAGE_SLUGS = Object.keys(FINANCE_PAGES);

export const FINANCE_PAGE_LABELS = {
  "commercial-mortgages": "Commercial Mortgages",
  "medical-practice-finance": "Doctors & Medical",
  "finance-for-accountants": "Accountants & CPAs",
  "dental-practice-finance": "Dentists",
  "professional-practice-finance": "Lawyers, Vets & Allied Health",
  "property-development-finance": "Property Developers",
  "business-acquisition-finance": "Business Acquisitions",
};
